import { BankStatementRow, Debt } from '../types';

export interface DebtPaymentMatch {
  rowId: string;
  debtId: string;
  debtName: string;
}

/**
 * Suggests (never auto-applies) that a plain bank-account debit might
 * actually be a payment toward a debt that has no Plaid-linked account of
 * its own to auto-detect payments against the way plaidSyncBalances does
 * for a linked credit card/LOC (see functions/src/plaid.ts) -- a mortgage
 * is the common case: Plaid can see the outgoing chequing withdrawal, but
 * there's no "mortgage" account on the other end for it to match against.
 *
 * Keyword hints are opt-in per debt (Debt.paymentMatchHint), so nothing
 * changes for anyone who hasn't set one. A keyword match alone is treated
 * as sufficient; the amount check is a soft extra filter only applied when
 * the debt has a real monthlyMinimum to compare against -- a mortgage
 * payment can drift slightly (escrow adjustments, extra principal), so a
 * tight tolerance would silently miss real matches.
 */
export function findDebtPaymentMatches(rows: BankStatementRow[], debts: Debt[]): DebtPaymentMatch[] {
  const hinted = debts
    .filter((d): d is Debt & { id: string; paymentMatchHint: string } => !!d.id && !!d.paymentMatchHint?.trim())
    .map(d => ({
      debt: d,
      keywords: d.paymentMatchHint.split(',').map(k => k.trim().toLowerCase()).filter(Boolean),
    }))
    .filter(h => h.keywords.length > 0);

  if (hinted.length === 0) return [];

  const matches: DebtPaymentMatch[] = [];
  for (const row of rows) {
    if (!row.id) continue;
    if (row.status !== 'new' && row.status !== 'possible_duplicate') continue;
    if (row.direction !== 'debit' || row.accountType !== 'bank_account') continue;

    const desc = row.description.toLowerCase();
    for (const { debt, keywords } of hinted) {
      if (!keywords.some(k => desc.includes(k))) continue;
      if (debt.monthlyMinimum > 0) {
        const tolerance = Math.max(debt.monthlyMinimum * 0.15, 50);
        if (Math.abs(row.amount - debt.monthlyMinimum) > tolerance) continue;
      }
      matches.push({ rowId: row.id, debtId: debt.id, debtName: debt.name });
      break; // first matching debt wins -- ambiguous double-hints are rare and not worth guessing between
    }
  }
  return matches;
}
